import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { CalendarIcon, Clock } from "lucide-react";

const SLOTS = ["10:00 AM", "11:30 AM", "2:00 PM", "3:30 PM", "5:00 PM"];
const CONSULTANTS = ["Legal Advocate", "Urban Planner", "Investment Strategist"];

export default function AdvisoryBooking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date(Date.now() + 24 * 3600 * 1000));
  const [slot, setSlot] = useState(SLOTS[0]);
  const [consultant, setConsultant] = useState(CONSULTANTS[0]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/mine").then(({ data }) => setMine(data)).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="mx-auto max-w-md py-24 px-6 text-center">
        <h1 className="font-display text-2xl font-semibold">Sign in to book</h1>
        <p className="text-slate-400 mt-2 text-sm">Advisory sessions require a registered account.</p>
        <Button className="mt-6 rounded-full bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => navigate("/login")}>Sign in</Button>
      </div>
    );
  }

  const submit = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        user_id: user.user_id, user_email: user.email, user_name: user.name,
        consultant_type: consultant, date: date.toISOString().slice(0, 10),
        time_slot: slot, notes, status: "pending",
      });
      toast.success("Advisory booked — we'll confirm shortly.");
      setMine([data, ...mine]);
      setNotes("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Advisory Desk</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Book a 1-on-1 strategy session</h1>
      <p className="mt-2 text-slate-400 text-sm max-w-2xl">Meet with our in-house advocates and urban planners to review parcel due-diligence, structure JD deals, or plan a corridor entry.</p>

      <div className="mt-10 grid lg:grid-cols-2 gap-8">
        <Card className="bg-[#1E293B] border-[#334155] p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3 flex items-center gap-2"><CalendarIcon className="h-3 w-3" />Choose a date</div>
          <Calendar
            data-testid={TID.bookingDate}
            mode="single" selected={date} onSelect={setDate}
            disabled={(d) => d < new Date(Date.now() - 24 * 3600 * 1000)}
            className="rounded-md border border-[#334155] bg-[#0F172A] text-white p-3"
          />
        </Card>

        <Card className="bg-[#1E293B] border-[#334155] p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Consultant</Label>
            <Select value={consultant} onValueChange={setConsultant}>
              <SelectTrigger data-testid={TID.bookingConsultant} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {CONSULTANTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Time slot</Label>
            <Select value={slot} onValueChange={setSlot}>
              <SelectTrigger data-testid={TID.bookingSlot} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Query</Label>
            <Textarea data-testid={TID.bookingNotes} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Share your corridor of interest, parcel IDs, or the questions you'd like discussed."
              className="mt-2 bg-[#0F172A] border-[#334155] text-white min-h-[110px]" />
          </div>
          <Button data-testid={TID.bookingSubmit} onClick={submit} disabled={busy} className="w-full h-11 rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
            {busy ? "Booking…" : "Confirm booking"}
          </Button>
        </Card>
      </div>

      <div className="mt-14">
        <h2 className="font-display text-xl font-semibold tracking-tight">Your bookings</h2>
        <div className="mt-4 space-y-3">
          {mine.length === 0 ? (
            <div className="text-sm text-slate-500">No bookings yet.</div>
          ) : mine.map((b) => (
            <div key={b.id} className="rounded-lg border border-[#334155] bg-[#1E293B] p-4 flex items-center justify-between">
              <div>
                <div className="text-white font-medium">{b.consultant_type}</div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-3"><CalendarIcon className="h-3 w-3" />{b.date}<Clock className="h-3 w-3" />{b.time_slot}</div>
              </div>
              <Badge className={b.status === "confirmed" ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}>{b.status}</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
